/**
 * The Update feed (`specs/M5-notifications.md` §8,
 * `discovery/ui-reference/notifications/inbox.png`).
 *
 * A **route**, not an overlay: F4 registered `/my_inbox` that way and owns the backdrop and
 * Escape; this is the modal's content, drawn to the measured 400 px rail / 982 px feed split.
 *
 * `Bookmarked`, `All account` and `Scheduled` are rendered as tabs — their measured geometry
 * is part of the strip — but their body says so rather than showing a silently wrong list.
 *
 * **P-286 (round 11c).** The strip used to be a browser-default `<select>` and a browser-default
 * `<button>`, and the card a plain box. `[ui:library/shell-07-inbox.png]` and
 * `[ui:notifications/inbox-show.png]` show what monday draws instead, and that is what is built
 * here: tabs with glyphs (`@`, a bookmark, a briefcase, a scheduled send), a **text-style**
 * `Show ▸ …` trigger that opens a 236 px menu with the selected row tinted, `Mark all as read`
 * as a tertiary button with a double tick, a 24 px blue read tick on the card's top-right corner
 * and a breadcrumb with board / group / item glyphs. Every `data-testid` and every behaviour
 * (including P-188's "always in the strip, disabled when there is nothing to mark" and P-283's
 * `onReadStateChange`) is unchanged — only the look is.
 *
 * **P-325 (round 11o).** The breadcrumb is now links — board, group, item — the way the sandbox
 * capture `discovery/ui-reference/notifications/11o-*` shows monday's (`FeedBreadcrumb.tsx`).
 */
import {
  AnchoredPopover,
  Icon,
  IconButton,
  PageEmptyState,
  PageSkeleton,
  RelativeTime,
  Tab,
  Tabs,
  icons,
  useOptionalToast,
} from '@elizmonday/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FeedBreadcrumb } from './FeedBreadcrumb';
import type { InboxFacets, InboxRowView, NotificationsSource } from './types';

export const INBOX_TABS = ['all_updates', 'mentioned', 'bookmarked', 'all_account', 'scheduled'] as const;
export type InboxTabId = (typeof INBOX_TABS)[number];
/** monday's labels, in monday's order and at the measured widths `[ui §3]`. */
export const INBOX_TAB_LABELS: Record<InboxTabId, string> = {
  all_updates: 'All updates',
  mentioned: 'I was mentioned',
  bookmarked: 'Bookmarked',
  all_account: 'All account',
  scheduled: 'Scheduled',
};
/**
 * The glyph each tab carries `[ui:library/shell-07-inbox.png]`. `All updates` carries none —
 * monday draws its label alone — so the map is deliberately partial.
 */
const INBOX_TAB_ICONS: Partial<Record<InboxTabId, (typeof icons)[keyof typeof icons]>> = {
  mentioned: icons.mention,
  bookmarked: icons.bookmark,
  all_account: icons.account,
  scheduled: icons.scheduled,
};
export const INBOX_TABS_IMPLEMENTED: readonly InboxTabId[] = ['all_updates', 'mentioned'];

/**
 * The `Show` menu, verbatim from `[ui:notifications/inbox-show.png]`: two rows, the first one
 * "Read and Unread updates" — *not* "All updates", which is the tab's name and would read as the
 * same control twice.
 */
export const INBOX_SHOW_OPTIONS = [
  { id: 'all', label: 'Read and Unread updates' },
  { id: 'unread', label: 'Unread updates' },
] as const;

/** The measured width of that menu `[ui:notifications/inbox-show.png]` (x 469 → 705). */
const SHOW_MENU_WIDTH = 236;

export interface InboxScreenProps {
  source: NotificationsSource;
  tab: InboxTabId;
  onTabChange: (tab: InboxTabId) => void;
  onClose?: () => void;
  /** Bumped by the realtime hook when an update lands. */
  revision?: number;
  /**
   * Called after this screen changed the feed's read state (P-283). The host invalidates the
   * top bar's `inbox.unreadCount` query with it, so the badge drops the moment `Mark all as
   * read` answers instead of waiting out its 15 s `staleTime` — which reads as "nothing
   * happened" to the person who just clicked it.
   */
  onReadStateChange?: () => void;
  /**
   * P-325: the host router's `navigate`, used by the breadcrumb's board / group / item links so a
   * click stays in the app instead of reloading it. Without it the links are ordinary `href`s.
   */
  onNavigate?: (href: string) => void;
}

const initials = (name: string | null): string =>
  (name ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';

export function InboxScreen({
  source,
  tab,
  onTabChange,
  onClose,
  revision = 0,
  onReadStateChange,
  onNavigate,
}: InboxScreenProps) {
  const [facets, setFacets] = useState<InboxFacets | null>(null);
  const [rows, setRows] = useState<InboxRowView[] | null>(null);
  const [boardId, setBoardId] = useState<string | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [showOpen, setShowOpen] = useState(false);
  /* The anchor is **state**, not a ref: `AnchoredPopover` measures the element it is handed at
     render time, and a ref that is filled after the first paint never triggers that render. */
  const [showAnchor, setShowAnchor] = useState<HTMLButtonElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useOptionalToast();

  const implemented = INBOX_TABS_IMPLEMENTED.includes(tab);

  const load = useCallback(async () => {
    try {
      const [nextFacets, feed] = await Promise.all([
        source.inboxFacets(),
        implemented
          ? source.inboxFeed({ tab, unreadOnly, ...(boardId ? { boardIds: [boardId] } : {}) })
          : Promise.resolve({ rows: [] as InboxRowView[], nextCursor: null }),
      ]);
      // A malformed or stubbed response must render as empty, never throw inside the shell.
      setFacets(nextFacets?.boards ? nextFacets : { total: 0, boards: [] });
      setRows(feed?.rows ?? []);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load the update feed');
    }
  }, [source, tab, unreadOnly, boardId, implemented]);

  useEffect(() => {
    void load();
  }, [load, revision]);

  // A refused write is reported, never swallowed: P-191 reached a user as "nothing happened"
  // because the optimistic patch greyed the rows and the rejection was caught and dropped.
  const markAllRead = useCallback(async () => {
    setRows((current) => current?.map((row) => ({ ...row, read: true })) ?? current);
    try {
      await source.inboxMarkAllRead(boardId ? [boardId] : undefined);
      onReadStateChange?.();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not mark the feed as read', { kind: 'negative' });
    }
    await load();
  }, [source, boardId, load, toast, onReadStateChange]);

  const markRead = useCallback(
    async (row: InboxRowView) => {
      setRows((current) => current?.map((entry) => (entry.id === row.id ? { ...entry, read: true } : entry)) ?? current);
      try {
        await source.inboxMarkRead([row.id]);
        onReadStateChange?.();
      } catch (cause) {
        toast(cause instanceof Error ? cause.message : 'Could not mark the update as read', { kind: 'negative' });
      }
      await load();
    },
    [source, load, toast, onReadStateChange],
  );

  const tabIndex = useMemo(() => Math.max(INBOX_TABS.indexOf(tab), 0), [tab]);
  const hasUnread = useMemo(() => (rows ?? []).some((row) => !row.read), [rows]);
  const showValue = unreadOnly ? 'unread' : 'all';
  const showLabel = INBOX_SHOW_OPTIONS.find((option) => option.id === showValue)?.label ?? '';

  return (
    <div className="ez-inbox" data-testid="inbox-modal">
      {onClose ? (
        <span className="ez-inbox__close">
          <IconButton
            icon={icons.close}
            ariaLabel="Close"
            kind="tertiary"
            onClick={onClose}
            data-testid="inbox-close"
          />
        </span>
      ) : null}

      <aside className="ez-inbox__rail" data-testid="inbox-rail">
        {/*
          P-283 (3): the top bar's counter caps at `99+` (`maxDigits={2}`, matching monday's own
          badge), so the exact number has to be readable *somewhere*. Here, where the person came
          to deal with it — never abbreviated.
        */}
        <h1 className="ez-inbox__title">
          <span>Update feed</span>
          <Icon icon={icons.comment} size={20} ignoreFocusStyle />
          {facets && facets.total > 0 ? (
            <span className="ez-inbox__unread" data-testid="inbox-unread-total">{`${facets.total} unread`}</span>
          ) : null}
        </h1>
        {/*
          monday puts a `See more` link next to this line; it opens a help-centre article that
          this deployment does not have, so the affordance is omitted rather than pointed at a
          dead URL (noted in the round's summary).
        */}
        <p className="ez-inbox__hint">What goes in my feed?</p>
        <div className="ez-inbox__rail-head">
          <h2>Filter by Board</h2>
          {/* The gear's target exists: Profile ▸ Notifications is `NotificationSettingsRoute`. */}
          <a className="ez-inbox__feed-settings" href="/me/notifications" data-testid="inbox-feed-settings">
            <Icon icon={icons.settings} size={16} ignoreFocusStyle />
            <span>Feed settings</span>
          </a>
        </div>
        <button
          type="button"
          className={`ez-inbox__board${boardId === null ? ' ez-inbox__board--selected' : ''}`}
          onClick={() => setBoardId(null)}
          data-testid="inbox-board-all"
        >
          <span>All boards in my feed</span>
          <span className="ez-inbox__count">{facets?.total ?? 0}</span>
        </button>
        {(facets?.boards ?? []).map((board) => (
          <button
            key={board.boardId}
            type="button"
            className={`ez-inbox__board${boardId === board.boardId ? ' ez-inbox__board--selected' : ''}`}
            onClick={() => setBoardId(board.boardId)}
            data-testid={`inbox-board-${board.boardId}`}
          >
            <span>{board.name}</span>
            <span className="ez-inbox__count">{board.count}</span>
          </button>
        ))}
      </aside>

      <section className="ez-inbox__feed">
        <div className="ez-inbox__tabs">
          <Tabs
            activeTabId={tabIndex}
            onTabChange={(index: number) => onTabChange(INBOX_TABS[index] ?? 'all_updates')}
            data-testid="inbox-tabs"
          >
            {INBOX_TABS.map((id) => (
              <Tab key={id} value={INBOX_TABS.indexOf(id)} icon={INBOX_TAB_ICONS[id]}>
                {INBOX_TAB_LABELS[id]}
              </Tab>
            ))}
          </Tabs>
        </div>

        <div className="ez-inbox__filters">
          {/*
            `[ui:notifications/inbox-show.png]`: a text-style trigger — bold `Show`, the current
            value in the regular weight, a chevron — that tints while its menu is open. It is a
            menu rather than a native `<select>` so the open state is monday's tinted list with
            the selected row highlighted, which an OS popup cannot be (P-286).
          */}
          <button
            ref={setShowAnchor}
            type="button"
            className="ez-inbox__show"
            data-testid="inbox-show"
            data-value={showValue}
            data-open={showOpen ? 'true' : undefined}
            aria-haspopup="menu"
            aria-expanded={showOpen}
            onClick={() => setShowOpen((open) => !open)}
          >
            <strong>Show</strong>
            <span>{showLabel}</span>
            <Icon icon={icons.chevronDown} size={16} ignoreFocusStyle />
          </button>
          <AnchoredPopover
            anchor={showAnchor}
            open={showOpen}
            onClose={() => setShowOpen(false)}
            label="Show"
            role="menu"
            width={SHOW_MENU_WIDTH}
            gap={0}
            data-testid="inbox-show-menu"
          >
            <div className="ez-inbox__show-menu">
              {INBOX_SHOW_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={showValue === option.id}
                  className="ez-inbox__show-option"
                  data-testid={`inbox-show-${option.id}`}
                  onClick={() => {
                    setUnreadOnly(option.id === 'unread');
                    setShowOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </AnchoredPopover>
          {/*
            P-188: it used to be rendered only while `Show ▸ Unread updates` was selected, so the
            feed's most-used action was two clicks and a filter away in the feed's default state.
            monday's capture (`notifications/inbox.png`) does not resolve whether its own button is
            conditional (q541), so the least-claim reading is taken: the button is **always in the
            strip** — its place is not a guess, the round asserts it in the default filter — and it
            is **disabled** when the feed has nothing left to mark, rather than disappearing.
          */}
          <button
            type="button"
            className="ez-inbox__mark-all"
            onClick={() => void markAllRead()}
            disabled={!hasUnread}
            data-testid="inbox-mark-all-read"
          >
            <Icon icon={icons.markAllRead} size={16} ignoreFocusStyle />
            <span>Mark all as read</span>
          </button>
        </div>

        <div className="ez-inbox__rows" data-testid="inbox-rows">
          {!implemented ? (
            <PageEmptyState
              title={`${INBOX_TAB_LABELS[tab]} is not available yet`}
              hint="The tab is here so its place in the strip is right; its store arrives later (M5 §8)."
              data-testid="inbox-tab-unavailable"
            />
          ) : error ? (
            <PageEmptyState title="The update feed is unavailable" hint={error} data-testid="inbox-error" />
          ) : rows === null ? (
            <PageSkeleton variant="list" rows={3} data-testid="inbox-loading" />
          ) : rows.length === 0 ? (
            <PageEmptyState
              title="No updates yet"
              hint="Updates on the boards you subscribe to land here."
              data-testid="inbox-empty"
            />
          ) : (
            rows.map((row) => (
              <article
                key={row.id}
                className="ez-inbox__card"
                data-testid={`inbox-update-${row.id}`}
                data-read={row.read ? 'true' : 'false'}
              >
                {/*
                  monday's control is a 24 px **filled blue tile with a white tick** riding on the
                  card's top-right corner, not an empty checkbox — so it is a button with the tick
                  glyph and an accessible name, which is also what keeps the behaviour (one click,
                  the row is read, the control is gone) exactly as it was.
                */}
                {row.read ? null : (
                  <button
                    type="button"
                    className="ez-inbox__read-tick"
                    aria-label="Mark as read"
                    onClick={() => void markRead(row)}
                    data-testid={`inbox-mark-read-${row.id}`}
                  >
                    <Icon icon={icons.checkSmall} size={16} ignoreFocusStyle />
                  </button>
                )}
                <div className="ez-inbox__card-head">
                  <span className="ez-notif-row__avatar" aria-hidden="true">
                    {row.creator?.photoUrl ? <img src={row.creator.photoUrl} alt="" /> : initials(row.creator?.name ?? null)}
                  </span>
                  <strong>{row.creator?.name ?? 'Someone'}</strong>
                  <RelativeTime value={row.createdAt} format="long" />
                </div>
                {/* P-325: every segment is a link, as on monday (`FeedBreadcrumb.tsx`). */}
                <FeedBreadcrumb row={row} {...(onNavigate ? { onNavigate } : {})} />
                {/* The body is sanitised server-side at write time (B7 §5), never here. */}
                <div className="ez-inbox__body" dangerouslySetInnerHTML={{ __html: row.bodyHtml }} />
                {/*
                  monday's row is `👍 Like · ↩ Reply`. Liking and replying are writes the feed's
                  port has no procedure for (`NotificationsSource` exposes reads and the two
                  read-state writes only), so the row carries the **counts** it does have rather
                  than two buttons that would do nothing.
                */}
                <div className="ez-inbox__card-footer">
                  <span className="ez-inbox__stat" data-testid={`inbox-likes-${row.id}`}>
                    <Icon icon={icons.like} size={16} ignoreFocusStyle />
                    {row.likeCount}
                  </span>
                  <span className="ez-inbox__stat" data-testid={`inbox-replies-${row.id}`}>
                    <Icon icon={icons.reply} size={16} ignoreFocusStyle />
                    {row.replyCount}
                  </span>
                </div>
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
